import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Users, Building2, UserCog, Activity, TrendingUp, Search, X,
  Check, Download, ChevronRight, Calendar, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics`;

const BUCKET_STYLE = {
  Cold:      { color: 'text-zinc-400',   bg: 'bg-zinc-500/10',   border: 'border-zinc-500/30' },
  Started:   { color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/30' },
  Activated: { color: 'text-emerald-400',bg: 'bg-emerald-500/10',border: 'border-emerald-500/30' },
  Strong:    { color: 'text-primary',    bg: 'bg-primary/15',    border: 'border-primary/40' },
};

const TAB_DEFS = {
  coach:  { label: 'Coaches',           icon: UserCog },
  parent: { label: 'Parents / Athletes',icon: Users },
  club:   { label: 'Clubs',             icon: Building2 },
};

const MILESTONES = {
  coach: [
    ['created_first_athlete',            'First Athlete'],
    ['invited_first_athlete',            'Inv. Athlete'],
    ['invited_first_parent',             'Inv. Parent'],
    ['created_first_drill',              'Drill'],
    ['created_first_training_plan',      'Plan'],
    ['scheduled_first_session',          'Session'],
    ['created_first_session_package',    'Package'],
    ['created_first_financial_record',   'Financial'],
    ['created_first_athlete_evaluation', 'Evaluation'],
    ['created_first_match_report',       'Match Report'],
    ['viewed_athlete_development_profile','Dev. Profile'],
    ['returned_after_7_days',            '+7 days'],
  ],
  parent: [
    ['completed_athlete_profile',  'Profile'],
    ['connected_with_coach',       'Coach Link'],
    ['viewed_radar',               'Radar'],
    ['opened_player_profile',      'Dev. Profile'],
    ['viewed_coach_feedback',      'Feedback'],
    ['viewed_training_plan',       'Train Plan'],
    ['viewed_match_insights',      'Match'],
    ['returned_after_7_days',      '+7 days'],
    ['returned_after_14_days',     '+14 days'],
  ],
  club: [
    ['created_first_team',           '1st Team'],
    ['added_first_coach',            '1st Coach'],
    ['added_first_athlete',          '1st Athlete'],
    ['invited_first_parent',         'Inv. Parent'],
    ['created_second_team',          '2nd Team'],
    ['created_match_report',         'Match Report'],
    ['viewed_dashboard',             'Dashboard'],
    ['viewed_player_intelligence',   'Intelligence'],
    ['used_filters',                 'Filters'],
    ['has_multiple_active_teams',    'Multi Active'],
    ['returned_after_7_days',        '+7 days'],
  ],
};

const formatDate = (iso) => {
  if (!iso || iso === 'true') return iso;
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

const PlatformAdminActivation = () => {
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState('coach');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [activationFilter, setActivationFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [drawerUser, setDrawerUser] = useState(null);
  const [drawerData, setDrawerData] = useState(null);

  const token = localStorage.getItem('meybra_token') || localStorage.getItem('platform_admin_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadSummary = async () => {
    try {
      const r = await axios.get(`${API}/summary`, { headers });
      setSummary(r.data);
    } catch (e) { /* silent */ }
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ kind: tab });
      if (activationFilter) params.append('activation', activationFilter);
      if (dateFrom) params.append('date_from', new Date(dateFrom).toISOString());
      if (dateTo) params.append('date_to', new Date(dateTo).toISOString());
      const r = await axios.get(`${API}/users?${params.toString()}`, { headers });
      setRows(r.data.rows || []);
    } catch (e) { setRows([]); }
    setLoading(false);
  };

  useEffect(() => { loadSummary(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadRows(); /* eslint-disable-next-line */ }, [tab, activationFilter, dateFrom, dateTo]);

  const filtered = useMemo(() => {
    if (!filterText) return rows;
    const q = filterText.toLowerCase();
    return rows.filter((r) =>
      [r.name, r.parent_name, r.club_name, r.email, r.business_name, r.athlete_name]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, filterText]);

  const openDrawer = async (row) => {
    setDrawerUser(row);
    setDrawerData(null);
    try {
      const r = await axios.get(`${API}/user/${tab}/${row.id}`, { headers });
      setDrawerData(r.data);
    } catch (e) { /* silent */ }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/export/${tab}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `meybra_${tab}_activation.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* silent */ }
  };

  return (
    <div className="min-h-screen text-white">
      <div className="mb-7">
        <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-3 py-1 mb-3">
          <Sparkles size={11} className="text-primary" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-primary">Founding Beta · Internal</span>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide">Activation Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-2">Who is activating, where they drop off, and who deserves a manual follow-up.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-7">
        {[
          { label: 'Total Users',       value: summary?.total_users ?? '—', icon: Users },
          { label: 'New This Week',     value: summary?.new_this_week ?? '—', icon: TrendingUp, accent: 'primary' },
          { label: 'Coaches',           value: summary?.coaches ?? '—', icon: UserCog },
          { label: 'Athletes / Parents',value: summary?.parents ?? '—', icon: Users },
          { label: 'Clubs',             value: summary?.clubs ?? '—', icon: Building2 },
          { label: 'Active (7d)',       value: summary?.active_users ?? '—', icon: Activity, accent: 'emerald' },
          { label: 'Activation Rate',   value: summary != null ? `${summary.activation_rate}%` : '—', icon: TrendingUp, accent: 'primary' },
        ].map((card, i) => (
          <div key={i} className="bg-card border border-border/30 rounded-sm p-3" data-testid={`activation-card-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <card.icon size={13} className={card.accent === 'primary' ? 'text-primary' : card.accent === 'emerald' ? 'text-emerald-400' : 'text-muted-foreground'} />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{card.label}</span>
            </div>
            <p className={`font-heading text-2xl ${card.accent === 'primary' ? 'text-primary' : 'text-white'}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Filters bar */}
      <div className="bg-card border border-border/30 rounded-sm p-4 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[180px] relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name, email..."
              className="pl-9 bg-zinc-900/60 border-border/30 text-sm"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              data-testid="activation-search"
            />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Calendar size={12} className="text-muted-foreground" />
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-zinc-900/60 border border-border/30 rounded-sm px-2 py-1 text-xs" />
            <span className="text-muted-foreground">→</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-zinc-900/60 border border-border/30 rounded-sm px-2 py-1 text-xs" />
          </div>
          <div className="flex items-center gap-1">
            <Filter size={12} className="text-muted-foreground" />
            {['', 'Cold', 'Started', 'Activated', 'Strong'].map((b) => (
              <button
                key={b || 'all'}
                onClick={() => setActivationFilter(b)}
                className={`px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-widest transition-all ${
                  activationFilter === b
                    ? 'bg-primary text-black font-bold'
                    : 'bg-zinc-900/60 border border-border/30 text-muted-foreground hover:border-primary/30'
                }`}
                data-testid={`activation-filter-${b || 'all'}`}
              >
                {b || 'All'}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={exportCsv} className="border-border/30 text-muted-foreground hover:text-primary uppercase tracking-wider text-xs" data-testid="activation-export-csv">
            <Download size={12} className="mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-border/30">
        {Object.entries(TAB_DEFS).map(([key, def]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2.5 text-sm uppercase tracking-wider flex items-center gap-2 border-b-2 transition-all ${
              tab === key ? 'text-primary border-primary' : 'text-muted-foreground border-transparent hover:text-white'
            }`}
            data-testid={`activation-tab-${key}`}
          >
            <def.icon size={14} /> {def.label}
            <span className="text-[10px] text-muted-foreground">({rows.length || '...'})</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-card border border-border/30 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 border-b border-border/30 text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3 sticky left-0 bg-zinc-900/95 z-10">User</th>
                <th className="text-left px-3 py-3">Email</th>
                <th className="text-left px-3 py-3">Signup</th>
                <th className="text-left px-3 py-3">Score</th>
                {MILESTONES[tab].map(([k, label]) => (
                  <th key={k} className="text-center px-2 py-3 whitespace-nowrap">{label}</th>
                ))}
                <th className="text-center px-2 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={20} className="p-8 text-center text-muted-foreground">Loading...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={20} className="p-8 text-center text-muted-foreground">No users yet for this filter.</td></tr>
              )}
              {filtered.map((row, i) => {
                const style = BUCKET_STYLE[row.bucket] || BUCKET_STYLE.Cold;
                const name = row.name || row.parent_name || row.club_name || row.email;
                return (
                  <tr
                    key={row.id}
                    className={`border-b border-border/10 hover:bg-zinc-900/40 cursor-pointer ${i % 2 === 0 ? '' : 'bg-zinc-900/20'}`}
                    onClick={() => openDrawer(row)}
                    data-testid={`activation-row-${row.id}`}
                  >
                    <td className="px-4 py-3 sticky left-0 bg-card z-10">
                      <div className="font-semibold text-white truncate max-w-[180px]">{name}</div>
                      {row.business_name && <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">{row.business_name}</div>}
                      {row.athlete_name && <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">Athlete: {row.athlete_name} ({row.athlete_age}y)</div>}
                      {row.num_teams != null && <div className="text-[10px] text-muted-foreground">{row.num_teams} teams · {row.approx_athlete_count || '?'} athletes</div>}
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground truncate max-w-[180px]">{row.email}</td>
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(row.signup_date)}</td>
                    <td className="px-3 py-3">
                      <div className={`inline-flex items-center gap-2 px-2 py-0.5 rounded-sm border ${style.bg} ${style.border}`}>
                        <span className={`font-heading text-sm ${style.color}`}>{row.score}</span>
                        <span className={`text-[9px] uppercase tracking-widest ${style.color}`}>{row.bucket}</span>
                      </div>
                    </td>
                    {MILESTONES[tab].map(([k, label]) => {
                      const v = row.milestones?.[k];
                      return (
                        <td key={k} className="px-2 py-3 text-center" title={v && v !== 'true' ? `Completed ${formatDate(v)}` : label}>
                          {v ? (
                            <Check size={14} className="text-emerald-400 mx-auto" />
                          ) : (
                            <span className="text-zinc-700">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-2 py-3 text-center">
                      <ChevronRight size={14} className="text-muted-foreground mx-auto" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer */}
      <AnimatePresence>
        {drawerUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex justify-end"
            onClick={() => { setDrawerUser(null); setDrawerData(null); }}
          >
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="w-full sm:max-w-md bg-[#0d0d0e] border-l border-border/30 h-full overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="activation-drawer"
            >
              <div className="p-5 border-b border-border/30 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-primary mb-1">User Detail</p>
                  <h2 className="font-heading text-lg uppercase tracking-wide truncate max-w-[300px]">
                    {drawerUser.name || drawerUser.parent_name || drawerUser.club_name}
                  </h2>
                  <p className="text-xs text-muted-foreground truncate">{drawerUser.email}</p>
                </div>
                <button className="p-1.5 rounded-full bg-zinc-900/60 border border-border/30 hover:border-primary/40" onClick={() => { setDrawerUser(null); setDrawerData(null); }} data-testid="activation-drawer-close">
                  <X size={14} />
                </button>
              </div>
              <div className="p-5 space-y-6">
                {/* Score */}
                <div className={`rounded-sm border p-4 ${BUCKET_STYLE[drawerUser.bucket].bg} ${BUCKET_STYLE[drawerUser.bucket].border}`}>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Activation Score</p>
                  <div className="flex items-baseline gap-2">
                    <span className={`font-heading text-4xl ${BUCKET_STYLE[drawerUser.bucket].color}`}>{drawerUser.score}</span>
                    <span className={`text-xs uppercase tracking-widest ${BUCKET_STYLE[drawerUser.bucket].color}`}>{drawerUser.bucket}</span>
                  </div>
                </div>

                {drawerData?.next_action && (
                  <div className="rounded-sm border border-primary/30 bg-primary/5 p-3">
                    <p className="text-[10px] uppercase tracking-widest text-primary mb-1">Suggested Next Action</p>
                    <p className="text-xs text-white leading-relaxed">{drawerData.next_action}</p>
                  </div>
                )}

                {drawerData?.onboarding && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Onboarding Answers</p>
                    <div className="bg-zinc-900/40 border border-border/20 rounded-sm p-3 space-y-1.5">
                      {Object.entries(drawerData.onboarding).filter(([k]) => !['id','created_at','phase','_id'].includes(k)).map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-2 text-xs">
                          <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</span>
                          <span className="text-white text-right truncate max-w-[200px]">{String(v ?? '—')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {drawerData?.milestones_detail && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                      Milestones ({drawerData.completed_count}/{drawerData.total_milestones})
                    </p>
                    <ul className="space-y-1.5">
                      {drawerData.milestones_detail.map((m) => (
                        <li key={m.key} className={`flex items-center justify-between gap-2 text-xs rounded-sm px-2.5 py-1.5 ${m.completed_at ? 'bg-emerald-500/5 border border-emerald-500/20' : 'bg-zinc-900/30 border border-border/10'}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            {m.completed_at ? <Check size={12} className="text-emerald-400 shrink-0" /> : <span className="w-3 h-3 rounded-full border border-zinc-700 shrink-0" />}
                            <span className={`truncate ${m.completed_at ? 'text-white' : 'text-muted-foreground'}`}>{m.label}</span>
                          </div>
                          <span className="text-[10px] text-muted-foreground shrink-0">
                            {m.completed_at && m.completed_at !== 'true' ? formatDate(m.completed_at) : `${m.weight} pts`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {drawerData?.timeline?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Timeline</p>
                    <ul className="space-y-1.5">
                      {drawerData.timeline.slice(0, 30).map((ev, i) => (
                        <li key={i} className="flex items-center justify-between gap-2 text-[11px] bg-zinc-900/30 border border-border/10 rounded-sm px-2.5 py-1.5">
                          <span className="text-muted-foreground truncate">{ev.event}</span>
                          <span className="text-zinc-500 shrink-0">{formatDate(ev.created_at)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlatformAdminActivation;
